<template>
<div class="toolbar">
    <transition name="fade" appear>
        <div class="sort-by-field field has-addons">
            <div class="control">
                <span :style="selectWidth" class="select is-small">
                    <select :disabled="isLoadingSuggestions" v-model="listType">
                        <option v-for="opt in listOpts" :value="opt.val" :key="opt.val">
                            {{ opt.text }}
                        </option>
                    </select>
                </span>
            </div>
            <div :title="sortDirDesc" class="control">
                <button
                    @click="toggleSortDir"
                    :class="{ 'is-static': isLoadingSuggestions }"
                    class="button is-small">
                    <span class="icon is-small">
                        <FaIcon :icon="sortIcon" size="lg" />
                    </span>
                </button>
            </div>
        </div>
    </transition>
    <transition name="fade">
        <button
            v-if="showNotification"
            @click="readNotifications"
            class="notification-button button is-small">
            <span class="icon is-small">
                <span class="badge is-top-left">
                    {{ numNotifications }}
                </span>
                <FaIcon icon="bell" size="lg"/>
            </span>
        </button>
    </transition>
</div>
</template>

<script>
import { mapGetters } from 'vuex'

import * as C from '@shared/constants'
import * as M from '@/store/mutations'

export default {
    name: 'Toolbar',
    computed: {
        ...mapGetters([
            'isAdmin',
            'isLoadingSuggestions',
            'numNotifications',
        ]),
        listType: {
            get: vm => vm.$store.state.suggestions.listType,
            set (listType) {
                const payload = { listType }
                // Change sortDir to desc when listType changes. It can be
                // confusing if the sortDir remains the same.
                if (this.sortDir !== C.SORT_DESC) {
                    payload.sortDir = C.SORT_DESC
                }

                this.$store.commit(M.SET_LIST_FILTERS, payload)
            }
        },
        sortDir: {
            get: vm => vm.$store.state.suggestions.sortDir,
            set (sortDir) {
                this.$store.commit(M.SET_LIST_FILTERS, { sortDir })
            }
        },
        isPendingList: vm => vm.listType === C.LIST_TYPE_PENDING,
        listOpts: vm => ([
            { val: C.LIST_TYPE_VOTES, text: vm.$t('top') },
            { val: C.LIST_TYPE_DATE, text: vm.$t('new') },
            { val: C.LIST_TYPE_BROADCASTER_LIKES, text: vm.$t('streamerLikes') },
            { val: C.LIST_TYPE_BROADCASTER_POSTS, text: vm.$t('streamerPosts') },
            { val: C.LIST_TYPE_ARCHIVED, text: vm.$t('archived') },
            { val: C.LIST_TYPE_USER, text: vm.$t('myPosts') },
            (vm.isAdmin ? { val: C.LIST_TYPE_PENDING, text: vm.$t('pending') } : null)
        ].filter(Boolean)), // filter out admin lists for non-admins
        sortIcon: vm => `sort-amount-${vm.sortDir === C.SORT_ASC ? 'up' : 'down'}`,
        sortDirDesc: vm => {
            const listTypeDesc = LANGUAGE[vm.listType] ?? LANGUAGE[C.LIST_TYPE_VOTES]
            const desc = listTypeDesc[vm.sortDir]
            return desc ? `Now showing ${desc}` : ''
        },
        // select gets expanded due to the "Streamer Likes" option, but
        // since the default option is "New", the extra space looks bad.
        selectWidth: vm => {
            const opt = vm.listOpts.find(o => o.val === vm.listType)
            const width = opt?.text.length >= 5 ? 'auto' : '70px'
            return { width }
        },
        showNotification: vm => vm.numNotifications > 0,
    },
    methods: {
        readNotifications () {
            this.$store.dispatch('readNotifications')
            ga('send', 'event', 'Button', 'Click', 'Notifications')
        },
        toggleSortDir () {
            this.sortDir = (this.sortDir === C.SORT_DESC) ? C.SORT_ASC : C.SORT_DESC
        }
    }
}

// "Now showing ${desc}"
const LANGUAGE = {
    [C.LIST_TYPE_VOTES]: {
        [C.SORT_ASC]: 'least upvoted posts',
        [C.SORT_DESC]: 'most upvoted posts',
    },
    [C.LIST_TYPE_DATE]: {
        [C.SORT_ASC]: 'oldest posts',
        [C.SORT_DESC]: 'newest posts',
    },
    [C.LIST_TYPE_BROADCASTER_LIKES]: {
        [C.SORT_ASC]: 'least upvoted posts',
        [C.SORT_DESC]: 'most upvoted posts',
    },
    [C.LIST_TYPE_USER]: {
        [C.SORT_ASC]: 'my oldest posts',
        [C.SORT_DESC]: 'my newest posts',
    },
    [C.LIST_TYPE_PENDING]: {
        [C.SORT_ASC]: 'oldest posts',
        [C.SORT_DESC]: 'newest posts',
    },
}
</script>

<style lang="scss" scoped>
.toolbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 8px;
    transition: background-color $default-transition-duration;
    border-bottom: 1px solid $border-color;
}
select, option, .dropdown-item {
    text-transform: capitalize;
}

.sort-by-field {
    margin-right: auto;
}

.notification-button {
    margin-left: auto;
    .badge {
        background: $notification-color;
    }
}

.field, .control {
    margin-bottom: 0 !important;
}

.icon {
    cursor: pointer;
}
</style>
